// ============================================================
// xFreeScan - API Client
// ============================================================

const API_BASE = import.meta.env.VITE_API_URL || 'https://xfreescan-api.aforero.workers.dev/api';

interface ApiOptions {
  method?: string;
  body?: unknown;
  token?: string;
}

export async function apiClient<T = any>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, token } = options;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const storedToken = token || localStorage.getItem('xfreescan_token');
  if (storedToken) headers['Authorization'] = `Bearer ${storedToken}`;

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method, headers, body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json();
  if (!response.ok) throw new ApiError(data.error || 'Error desconocido', response.status, data.details);
  return data;
}

export class ApiError extends Error {
  status: number;
  details?: any;
  constructor(message: string, status: number, details?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

export const authApi = {
  register: (data: { email: string; nombre_empresa: string; movil: string; pais: string; contacto_nombre?: string }) =>
    apiClient('/auth/register', { method: 'POST', body: data }),
  login: (data: { email: string }) =>
    apiClient('/auth/login', { method: 'POST', body: data }),
  verifyOtp: (data: { email: string; code: string }) =>
    apiClient('/auth/verify-otp', { method: 'POST', body: data }),
  resendOtp: (data: { email: string }) =>
    apiClient('/auth/resend-otp', { method: 'POST', body: data }),
  getDevOtp: (email: string) =>
    apiClient(`/auth/dev-otp/${encodeURIComponent(email)}`),
};

export const scansApi = {
  create: (data: {
    target: string;
    scan_types: string[];
    nombre?: string;
    consent_active?: boolean;
    /**
     * Contexto de caja gris/blanca. El backend deriva scan_mode de aqui y
     * separa el material sensible antes de persistir — ver
     * api/src/utils/scan-context.ts.
     */
    context?: Array<{ kind: string; [key: string]: unknown }>;
  }) => apiClient('/scans', { method: 'POST', body: data }),
  list: (params?: { page?: number; limit?: number; status?: string }) => {
    const q = new URLSearchParams();
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.status) q.set('status', params.status);
    return apiClient(`/scans?${q.toString()}`);
  },
  get: (id: string) => apiClient(`/scans/${id}`),
  getFindings: (id: string, params?: { severity?: string; scan_type?: string }) => {
    const q = new URLSearchParams();
    if (params?.severity) q.set('severity', params.severity);
    if (params?.scan_type) q.set('scan_type', params.scan_type);
    return apiClient(`/scans/${id}/findings?${q.toString()}`);
  },
  getReport: (id: string) => apiClient(`/scans/${id}/report`),
};

export const dashboardApi = {
  getStats: () => apiClient('/dashboard/stats'),
  getUser: () => apiClient('/dashboard/user'),
};
